import { type JSX,useCallback, useEffect } from 'react';

import { Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import {
    selectPageInfo,
    selectPaginationGridData,
    setPaginationGridData
} from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ClaimValueField } from 'common/components/sharedCustomComponents/Claims/ClaimValueField';
import { IUser, UserProfileSearchDropdown } from 'common/components/sharedCustomComponents/userProfileSearchDropdown/UserProfileSearchDropdown';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import {
    ClaimUserThunkPostParams,
    ClaimUserThunkPostResponse,
    ClaimUserThunkPutParams,
    ClaimUserThunkPutResponse,
    fetchClaimUserMemberships,
    postClaimUserMembership,
    putClaimUserMembership } from 'common/services/api/claimUserMembership/claimUserMembership.thunk';
import { Claim } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { selectFilteredStatus, selectFilters } from './claimUserMembershipSlice';
import { ClaimUserMembershipUserProfileInfoField } from './ClaimUserMembershipUserProfileInfoField';
import { useClaimValueInput } from './hooks/useClaimValueInput';
import { useUserProfileSearchInput } from './hooks/useUserProfileSearchInput';
import { useValidateLinkFields } from './hooks/useValidateLinkFields';

type NotFoundCaseProps = {
    tenantId: string,
    claimId: string,
    snackbarMessage: string,
    page: number
};

const SUCCESS_POST_MESSAGE = 'The User was successfully linked.';
const EXISTING_LINK_ERROR_MESSAGE = 'This User is already linked to this Claim.';
const UNKNOWN_REASON_LINKING_ERROR_MESSAGE = 'There was an error while linking the User.' + DEFAULT_ERROR_MESSAGE;
const UNKNOWN_REASON_EDIT_ERROR_MESSAGE = 'There was an error while modifying the Claim.' + DEFAULT_ERROR_MESSAGE;
const EXISTING_CLAIM_VALUE_ERROR_MESSAGE = 'The Claim value was not changed';

interface ClaimUserItem {
    claimValue: string;
}

interface ClaimUserDrawerProperties {
    tenantId: string;
    tenantName: string;
    claim: Claim;
}

function useClaimUserDrawer(
    entityName: string,
    drawerProps: ClaimUserDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<IUser & ClaimUserItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const drawerContext = useDrawerContext<
        IUser & ClaimUserItem,
        ClaimUserDrawerProperties,
        ClaimUserThunkPostParams,
        ClaimUserThunkPostResponse,
        ClaimUserThunkPutParams,
        ClaimUserThunkPutResponse
    >(entityName, drawerProps, editingItemInfo);

    const paginationInfo = useAppSelector(selectPaginationGridData) ?? { currentPage: 0, rowsPerPage: 0 };
    const currentPage = paginationInfo.currentPage ?? 0;
    const rowsPerPage = paginationInfo.rowsPerPage ?? 10;
    const keywords = useAppSelector(selectFilters).keywords ?? [];
    const filteredStatus = useAppSelector(selectFilteredStatus) ?? [];

    const claimValueInput = useClaimValueInput(
        drawerContext.isOpenDrawer,
        editingItemInfo?.editingRow?.claimValue ?? '',
        drawerContext.action === Operations.Edit);
    const userSearchInput = useUserProfileSearchInput();

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.User && drawerContext.action === Operations.Link) {
            userSearchInput.resetUserInfo();
            claimValueInput.handleClaimValueChange('');
            dispatch(setSaveButtonEnabled(false));
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, drawerContext.action, dispatch]);

    useValidateLinkFields({
        isDrawerOpened: drawerContext.isOpenDrawer,
        isLinkOperation: drawerContext.action === Operations.Link,
        validationFields: {
            userInfo: userSearchInput.userInfo,
            claimValue: claimValueInput.claimValue
        },
        claimDataTypeName: drawerProps.claim.claimDataTypeName
    });

    const fetchClaimUserMembershipGridData = useCallback((tenantId: string, claimId: string) => {
        dispatch(fetchClaimUserMemberships({
            tenantId,
            claimId,
            skip: 0,
            take: rowsPerPage,
            search: keywords[0],
            sortKey: paginationInfo.sortModel?.[0]?.field,
            sortOrder: paginationInfo.sortModel?.[0]?.sort,
            status: filteredStatus
        }))
            .unwrap()
            .then((result) => {
                dispatch(setPaginationGridData({
                    rows: result.data,
                    rowsCount: result.count,
                    currentPage: result.skipped / rowsPerPage,
                    rowsPerPage: rowsPerPage,
                    sortModel: paginationInfo.sortModel
                }));
            })
            .catch(() => {
                dispatch(setPaginationGridData({}));
            })
            .finally(() => {
                enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                dispatch(closeDrawer());
            });
    }, [dispatch, enqueueSnackbar, rowsPerPage, keywords, paginationInfo.sortModel, filteredStatus]);

    const handleNotFoundCaseWhenSavingData = useCallback(({
        tenantId,
        claimId,
        snackbarMessage,
        page
    }: NotFoundCaseProps) => {
        if (page === 0) {
            enqueueSnackbar(snackbarMessage, { variant: 'success' });
            dispatch(setPaginationGridData({}));
            dispatch(closeDrawer());
        }
        fetchClaimUserMembershipGridData(tenantId, claimId);
    }, [dispatch, enqueueSnackbar, fetchClaimUserMembershipGridData]);

    const getPostOptions = useCallback((): ClaimUserThunkPostParams => ({
        tenantId: drawerProps.tenantId,
        claimId: drawerProps.claim.id,
        userId: userSearchInput.userInfo?.id ?? '',
        value: claimValueInput.claimValue,
        paginationInfo: {
            skip: currentPage * rowsPerPage,
            take: rowsPerPage,
            search: keywords[0],
            sortModel: paginationInfo.sortModel,
        },
        status: filteredStatus
    }), [drawerProps, userSearchInput.userInfo?.id, claimValueInput.claimValue, currentPage, rowsPerPage, keywords, paginationInfo.sortModel, filteredStatus]);

    const getPutOptions = useCallback((): ClaimUserThunkPutParams => {
        claimValueInput.setIsClaimValueInConflict(false);
        return {
            tenantId: drawerProps.tenantId,
            claimId: drawerProps.claim.id,
            userId: editingItemInfo?.editingItemId ? String(editingItemInfo.editingItemId) : '',
            value: claimValueInput.claimValue,
            paginationInfo: {
                skip: currentPage * rowsPerPage,
                take: rowsPerPage,
                search: keywords[0],
                sortModel: paginationInfo.sortModel,
            },
            status: filteredStatus
        };
    }, [drawerProps, editingItemInfo?.editingItemId, claimValueInput, currentPage, rowsPerPage, keywords, paginationInfo.sortModel, filteredStatus]);

    const createSuccessHandler = useCallback((postResult: ClaimUserThunkPostResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setPaginationGridData({
            rows: postResult.rows,
            rowsCount: postResult.count,
            currentPage: postResult.skipped / rowsPerPage,
            rowsPerPage: rowsPerPage,
        }));
    }, [drawerContext, dispatch, rowsPerPage]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        const postOptions = getPostOptions();
        const { tenantId, claimId } = postOptions;
        dispatch(setNameInConflict(false));
        switch (extractStatusCode(error.message)) {
            case 404: {
                handleNotFoundCaseWhenSavingData({
                    tenantId,
                    claimId,
                    snackbarMessage: SUCCESS_POST_MESSAGE,
                    page: currentPage
                });
                break;
            }
            case 409: {
                dispatch(setNameInConflict(true));
                break;
            }
            default: {
                enqueueSnackbar(
                    UNKNOWN_REASON_LINKING_ERROR_MESSAGE,
                    { variant: 'error' }
                );
                break;
            }
        }
    }, [dispatch, enqueueSnackbar, getPostOptions, handleNotFoundCaseWhenSavingData, currentPage]);

    const editSuccessHandler = useCallback((putResult: ClaimUserThunkPutResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setPaginationGridData({
            rows: putResult.rows,
            rowsCount: putResult.count,
            currentPage: putResult.skipped / rowsPerPage,
            rowsPerPage: rowsPerPage,
        }));
    }, [drawerContext, dispatch, rowsPerPage]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editFailureHandler = useCallback((error: any) => {
        const putOptions = getPutOptions();
        const { tenantId, claimId } = putOptions;
        dispatch(setNameInConflict(false));
        switch (extractStatusCode(error.message)) {
            case 404: {
                handleNotFoundCaseWhenSavingData({
                    tenantId,
                    claimId,
                    snackbarMessage: DEFAULT_SUCCESS_MESSAGE,
                    page: currentPage
                });
                break;
            }
            case 409: {
                claimValueInput.setIsClaimValueInConflict(true);
                dispatch(setSaveButtonEnabled(false));
                break;
            }
            default: {
                enqueueSnackbar(
                    UNKNOWN_REASON_EDIT_ERROR_MESSAGE,
                    { variant: 'error' }
                );
                break;
            }
        }
    }, [dispatch, enqueueSnackbar, getPutOptions, handleNotFoundCaseWhenSavingData, currentPage, claimValueInput]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postClaimUserMembership, getPostOptions, createSuccessHandler, createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putClaimUserMembership, getPutOptions, editSuccessHandler, editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, editFailureHandler]);

    return {
        ...drawerContext,
        claimValueInput,
        userSearchInput,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    postCallback,
    drawerProps,
    userSearchInput,
    claimValueInput,
    dispatch
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    postCallback: () => void;
    drawerProps: ClaimUserDrawerProperties;
    userSearchInput: ReturnType<typeof useUserProfileSearchInput>;
    claimValueInput: ReturnType<typeof useClaimValueInput>;
    dispatch: ReturnType<typeof useAppDispatch>;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <UserProfileSearchDropdown
                    value={userSearchInput.userInfo}
                    onChange={(e: unknown, userInfo: IUser | null) => {
                        userSearchInput.handleChangeUserProfile(e, userInfo);
                        dispatch(setNameInConflict(false));
                    }}
                    hasError={nameInConflict}
                />
                {nameInConflict &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={EXISTING_LINK_ERROR_MESSAGE}
                    />
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Claim Name"
                    variant="filled"
                    defaultValue={drawerProps.claim.name}
                    disabled
                />
                <ClaimValueField
                    claim={drawerProps.claim}
                    value={claimValueInput.claimValue}
                    onChange={claimValueInput.handleClaimValueChange}
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    putCallback,
    drawerProps,
    editingItemInfo,
    claimValueInput
}: Readonly<{
    entityName: string;
    processing: boolean;
    putCallback: () => void;
    drawerProps: ClaimUserDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<IUser & ClaimUserItem>['editingItemInfo'];
    claimValueInput: ReturnType<typeof useClaimValueInput>;
}>): JSX.Element {
    const userProfile = editingItemInfo?.editingRow?.profile;
    const userFullName = `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`;
    const userEmail = `${userProfile?.email ?? ''}`;

    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ClaimUserMembershipUserProfileInfoField
                    name={userFullName}
                    email={userEmail}
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={drawerProps.tenantName}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Claim Name"
                    variant="filled"
                    defaultValue={drawerProps.claim.name}
                    disabled
                />
                <ClaimValueField
                    claim={drawerProps.claim}
                    value={claimValueInput.claimValue}
                    onChange={claimValueInput.handleClaimValueChange}
                    hasError={claimValueInput.isClaimValueInConflict}
                />
                {claimValueInput.isClaimValueInConflict &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={EXISTING_CLAIM_VALUE_ERROR_MESSAGE}
                    />
                }
            </Stack>
        </EditDrawer>
    );
}

export function ClaimUserMembershipCreateDrawerContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<IUser & ClaimUserItem>>) {
    const dispatch = useAppDispatch();
    const pageInfo = useAppSelector(selectPageInfo);
    const claimUserDrawerProps: ClaimUserDrawerProperties = {
        tenantId: pageInfo?.tenantId ?? '',
        tenantName: pageInfo?.tenantName ?? '',
        claim: {
            id: pageInfo?.id ?? '',
            name: pageInfo?.name ?? '',
            tenantId: pageInfo?.tenantId ?? '',
            claimDataTypeId: pageInfo?.claimDataTypeId ?? '',
            claimDataTypeName: pageInfo?.claimDataTypeName ?? ''
        }
    };

    const drawerState = useClaimUserDrawer(title, claimUserDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                postCallback={drawerState.postCallback}
                drawerProps={claimUserDrawerProps}
                userSearchInput={drawerState.userSearchInput}
                claimValueInput={drawerState.claimValueInput}
                dispatch={dispatch}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                putCallback={drawerState.putCallback}
                drawerProps={claimUserDrawerProps}
                editingItemInfo={editingItemInfo}
                claimValueInput={drawerState.claimValueInput}
            />
        );
    }

    return <></>;
}
